import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import BASE_URL, { IMAGE_DOMAIN } from '../../../config/config';
import Loader from '../../../components/Loader';
import FiltroBusqueda from '../../../components/ui/FiltroBusqueda';
import Popup from '../../../components/Popup';
import CropperModal from '../../../components/ui/CropperModal';
import useCropper from '../../../components/ui/useCropper';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { DEPTO_LABELS } from '../../../constants/usuarios';

const toggleArrayValue = (arr, value, setter) => {
  setter(arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]);
};

const GestorUsuarios = () => {
    const [showSidebar, setShowSidebar] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [rolesSeleccionados, setRolesSeleccionados] = useState([]);
  const [estadosSeleccionados, setEstadosSeleccionados] = useState([]);
  const [deptosSeleccionados, setDeptosSeleccionados] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1);
  const [usuariosPorPagina, setUsuariosPorPagina] = useState(12);

  const [seleccionados, setSeleccionados] = useState(new Set());
  const [procesandoBulk, setProcesandoBulk] = useState(false);

  const token = localStorage.getItem('AuthToken');
  const navigate = useNavigate();
  const usuarioActual = token ? jwtDecode(token) : null;
  const usuarioRole = usuarioActual?.role || null;
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ usuario: '@', contrasena: '', nombre: '', apellidos: '', telefono: '', email: '', webs_del_cliente: '', role: 'user' });
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [createdResult, setCreatedResult] = useState(null);
  const [repetirContrasena, setRepetirContrasena] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const fotoInputRef = useRef(null);

  const { rawImageSrc, showCropper, crop, setCrop, zoom, setZoom,
    onCropComplete, openCropper, cancelCrop, applyCrop } = useCropper({
    onCropDone: ({ blob, previewUrl }) => {
      // Revocar preview anterior
      if (fotoPreview) { try { URL.revokeObjectURL(fotoPreview); } catch { /* noop */ } }
      setFotoFile(blob);
      setFotoPreview(previewUrl);
    }
  });

  const obtenerUsuarios = async () => {
      try {
        const peticionUsuarios = axios.get(`${BASE_URL}/todos-usuarios`, {
          headers: { Authorization: token }
        });
        const esperaMinima = new Promise(resolve => setTimeout(resolve, 1000));
        const [response] = await Promise.all([peticionUsuarios, esperaMinima]);

        if (Array.isArray(response.data.usuarios)) {
          const ordenados = response.data.usuarios.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setUsuarios(ordenados);
          setUsuariosFiltrados(ordenados);
        } else {
          setUsuarios([]);
          setUsuariosFiltrados([]);
        }
      } catch (error) {
        console.error('Error al obtener usuarios:', error);
      } finally {
        setCargando(false);
      }
  };

  useEffect(() => {
    obtenerUsuarios();
  }, [token]);

  useEffect(() => {
    const q = busqueda.toLowerCase();
    const filtrados = usuarios.filter(user =>
      (user.nombre?.toLowerCase().includes(q) ||
        user.apellidos?.toLowerCase().includes(q) ||
        user.usuario?.toLowerCase().includes(q) ||
        user.email?.toLowerCase().includes(q)) &&
      (rolesSeleccionados.length === 0 || rolesSeleccionados.includes(user.role)) &&
      (estadosSeleccionados.length === 0 || estadosSeleccionados.includes(user.estado || 'verificado')) &&
      (deptosSeleccionados.length === 0 || deptosSeleccionados.includes(user.dpto_tecnico || 'no_asignado'))
    );
    setUsuariosFiltrados(filtrados);
    setPaginaActual(1);
  }, [busqueda, rolesSeleccionados, estadosSeleccionados, deptosSeleccionados, usuarios]);

  // Conteos por categoría (sobre el total, no sobre lo ya filtrado)
  const contarPorValor = (obtenerValor) => {
    const counts = {};
    usuarios.forEach(u => {
      const v = obtenerValor(u);
      counts[v] = (counts[v] || 0) + 1;
    });
    return counts;
  };
  const conteoRoles = contarPorValor(u => u.role);
  const conteoEstados = contarPorValor(u => u.estado || 'verificado');
  const conteoDeptos = contarPorValor(u => u.dpto_tecnico || 'no_asignado');

  // Cerrar sidebar con Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showSidebar) {
        setShowSidebar(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showSidebar]);

  // Limpiar todos los filtros del panel de usuarios
  const limpiarFiltros = () => {
    setBusqueda('');
    setRolesSeleccionados([]);
    setEstadosSeleccionados([]);
    setDeptosSeleccionados([]);
    setPaginaActual(1);
  };

  // Selección múltiple / acciones masivas
  const toggleSeleccionado = (usuario) => {
    setSeleccionados(prev => {
      const next = new Set(prev);
      if (next.has(usuario)) next.delete(usuario);
      else next.add(usuario);
      return next;
    });
  };

  const todosPaginaSeleccionados = usuariosActuales => usuariosActuales.length > 0 && usuariosActuales.every(u => seleccionados.has(u.usuario));

  const toggleSeleccionarTodos = (usuariosActuales) => {
    setSeleccionados(prev => {
      const next = new Set(prev);
      if (todosPaginaSeleccionados(usuariosActuales)) {
        usuariosActuales.forEach(u => next.delete(u.usuario));
      } else {
        usuariosActuales.forEach(u => next.add(u.usuario));
      }
      return next;
    });
  };

  const aplicarCambioMasivo = async (campo, valor) => {
    if (seleccionados.size === 0 || !valor) return;
    setProcesandoBulk(true);
    const handles = Array.from(seleccionados);
    try {
      await Promise.all(handles.map(usuarioHandle => {
        const u = usuarios.find(x => x.usuario === usuarioHandle);
        if (!u) return Promise.resolve();
        return axios.put(`${BASE_URL}/usuario/update/${usuarioHandle}`, { ...u, [campo]: valor }, {
          headers: { Authorization: token }
        });
      }));
      toast.success(`${handles.length} usuario${handles.length > 1 ? 's' : ''} actualizado${handles.length > 1 ? 's' : ''} correctamente.`);
      setSeleccionados(new Set());
      await obtenerUsuarios();
    } catch (error) {
      console.error('Error en acción masiva:', error);
      toast.error('Hubo un error al actualizar algunos usuarios.');
    } finally {
      setProcesandoBulk(false);
    }
  };

  const exportarCSV = () => {
    const encabezados = ['usuario', 'nombre', 'apellidos', 'email', 'telefono', 'role', 'estado', 'createdAt'];
    const filas = usuariosFiltrados.map(u => encabezados.map(campo => {
      const valor = u[campo] ?? '';
      return `"${String(valor).replace(/"/g, '""')}"`;
    }).join(','));
    const csv = [encabezados.join(','), ...filas].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usuarios_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Manejo del formulario de creación
  const abrirCrear = () => {
    setForm({ usuario: '@', contrasena: '', nombre: '', apellidos: '', telefono: '', email: '', webs_del_cliente: '', role: 'user' });
    // revoke any previous preview
    if (fotoPreview) {
      try { URL.revokeObjectURL(fotoPreview); } catch (err) { /* ignore */ }
    }
    setFotoFile(null);
    setFotoPreview(null);
    setShowCreatePopup(true);
  };

  const handleFormChange = (field, value) => {
    // For usuario field, ensure it always starts with '@'
    if (field === 'usuario') {
      let v = value || '';
      // Remove leading spaces
      v = v.replace(/^\s+/, '');
      // Remove any @ characters typed by the user to avoid duplicates
      const cleaned = v.replace(/@/g, '');
      const final = cleaned === '' ? '@' : '@' + cleaned;
      setForm(prev => ({ ...prev, usuario: final }));
      return;
    }
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) openCropper(f);
    e.target.value = '';
  };

  // Cerrar y limpiar el popup de creación
  const cerrarCrear = () => {
    if (fotoPreview) { try { URL.revokeObjectURL(fotoPreview); } catch { /* noop */ } }
    setFotoFile(null);
    setFotoPreview(null);
    setRepetirContrasena('');
    setShowPassword(false);
    setShowRepeatPassword(false);
    setForm({ usuario: '@', contrasena: '', nombre: '', apellidos: '', telefono: '', email: '', webs_del_cliente: '', role: 'user' });
    setShowCreatePopup(false);
  };

  // Cerrar y limpiar el popup de confirmación
  const cerrarConfirm = () => {
    setCreatedResult(null);
    setShowConfirmPopup(false);
  };

  // Revocar preview al desmontar o cuando cambie para evitar fugas de memoria
  useEffect(() => {
    return () => {
      if (fotoPreview) {
        try { URL.revokeObjectURL(fotoPreview); } catch (err) { /* ignore */ }
      }
    };
  }, [fotoPreview]);

  const submitCrearUsuario = async () => {
    // validaciones basicas: todos los campos obligatorios
    const requiredFields = ['usuario','nombre','apellidos','email','telefono','webs_del_cliente','role'];
    for (const f of requiredFields) {
      if (!form[f] || (typeof form[f] === 'string' && form[f].trim() === '')) {
        toast.error('Por favor rellena todos los campos obligatorios.');
        return;
      }
    }

    // contraseña y repetir
    if (!form.contrasena || form.contrasena.trim() === '') {
      toast.error('La contraseña es obligatoria o usa "Generar contraseña".');
      return;
    }
    if (form.contrasena !== repetirContrasena) {
      toast.error('Las contraseñas no coinciden.');
      return;
    }

    // La foto de perfil es opcional

    setCreating(true);
    try {
      const fd = new FormData();
      const cleanedUsuario = (form.usuario || '').toString().trim().replace(/^@+/, '');
      if (!cleanedUsuario || cleanedUsuario.length < 1) {
        toast.error('El usuario es obligatorio. Escribe un nombre de usuario después de @.');
        setCreating(false);
        return;
      }
      fd.append('usuario', cleanedUsuario);
      if (form.contrasena) fd.append('contrasena', form.contrasena);
      fd.append('nombre', form.nombre);
      fd.append('apellidos', form.apellidos);
      fd.append('telefono', form.telefono);
      fd.append('email', form.email);
      fd.append('webs_del_cliente', form.webs_del_cliente);
      fd.append('role', form.role);
      if (fotoFile) fd.append('fotoPerfil', fotoFile);

      const resp = await axios.post(`${BASE_URL}/crear-usuario`, fd, {
        headers: { Authorization: token, 'Content-Type': 'multipart/form-data' }
      });

      if (resp.data && resp.data.ok) {
        // displayPassword: prefer the tempPassword returned by backend, otherwise use the password that was in the form
        const displayPassword = resp.data.tempPassword || form.contrasena || '';
        setCreatedResult({ ...resp.data, displayPassword });
        // cerrar y limpiar el formulario de creación
        cerrarCrear();
        setShowConfirmPopup(true);
        toast.success('Usuario creado correctamente');
        // refrescar lista
        await obtenerUsuarios();
      } else {
        toast.error(resp.data && resp.data.message ? resp.data.message : 'Error al crear usuario');
      }
    } catch (error) {
      console.error('Error al crear usuario:', error);
      const msg = error.response?.data?.message || 'Error al crear usuario';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const copiarTextoBienvenida = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success('Texto copiado al portapapeles');
    } catch (e) {
      console.error('No se pudo copiar:', e);
      toast.error('No se pudo copiar el texto');
    }
  };

  const generarContrasena = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';
    let pw = '';
    const len = 12;
    for (let i = 0; i < len; i++) {
      pw += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setForm(prev => ({ ...prev, contrasena: pw }));
    setRepetirContrasena(pw);
    toast.info('Contraseña generada automáticamente');
  };

  const totalPaginas = Math.ceil(usuariosFiltrados.length / usuariosPorPagina);
  const indiceUltimo = paginaActual * usuariosPorPagina;
  const indicePrimero = indiceUltimo - usuariosPorPagina;
  const usuariosActuales = usuariosFiltrados.slice(indicePrimero, indiceUltimo);

  const cambiarPagina = (numeroPagina) => {
    if (numeroPagina >= 1 && numeroPagina <= totalPaginas) {
      setPaginaActual(numeroPagina);
    }
  };

  const irADetalle = (usuario) => {
    try {
      navigate(`/dashboard/usuarios/${usuario}`);
    } catch (error) {
      console.error('Error al navegar:', error);
    }
  };

  if (cargando) {
    return <div className="loader-fullscreen"><Loader /></div>;
  }

  return (
    <main className="site-width">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="contenedor-gestor-usuarios">
        <button className="boton-volver" onClick={() => navigate('/dashboard')}>
          ← Volver
        </button>
        <div className="pagina-cabecera">
          <h2>Gestión de usuarios</h2>
          <p>Administra los ajustes, estados y servicios de los usuarios.</p>
        </div>

        <div className="filtro-tickets-admin">
          <input
            type="text"
            placeholder="Buscar usuario por nombre, email o usuario..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
          <div className="filtros-acciones">
            <select
              className="selector-page-size"
              value={usuariosPorPagina}
              onChange={e => { setUsuariosPorPagina(Number(e.target.value)); setPaginaActual(1); }}
              title="Usuarios por página"
            >
              <option value={12}>12 / página</option>
              <option value={24}>24 / página</option>
              <option value={50}>50 / página</option>
            </select>
            <button type="button" className="chip-toggle" onClick={exportarCSV}>
              Exportar CSV
            </button>
            <button
              className="boton-toggle-filtros"
              onClick={() => setShowSidebar(!showSidebar)}
            >
              + Filtros
            </button>
            <button className="boton-crear-usuario" onClick={abrirCrear}>Crear usuario</button>
          </div>
        </div>

        <div className={`sidebar-filtros-overlay ${showSidebar ? 'open' : ''}`} onClick={() => setShowSidebar(false)} />
        <aside className={`sidebar-filtros${showSidebar ? ' open' : ''}`}>
          <FiltroBusqueda
            title="Filtrar usuarios"
            value={busqueda}
            onChange={setBusqueda}
            placeholder="Buscar usuario por nombre, email o usuario..."
            className="sidebar-filtros-content"
            showInput={false}
            onClose={() => setShowSidebar(false)}
            showFooter={true}
            onReset={limpiarFiltros}
            resetText="Resetear filtros"
          >
            <div className="grupo-filtro grupo-filtro-checkbox">
              <label>Rol</label>
              {[
                { value: 'user', label: 'Usuario' },
                { value: 'tecnico', label: 'Técnico' },
                { value: 'admin', label: 'Administrador' },
              ].map(opt => (
                <label key={opt.value} className="checkbox-filtro">
                  <input
                    type="checkbox"
                    checked={rolesSeleccionados.includes(opt.value)}
                    onChange={() => toggleArrayValue(rolesSeleccionados, opt.value, setRolesSeleccionados)}
                  />
                  <span>{opt.label}</span>
                  <span className="checkbox-filtro-count">{conteoRoles[opt.value] || 0}</span>
                </label>
              ))}
            </div>

            <div className="grupo-filtro grupo-filtro-checkbox">
              <label>Estado</label>
              {[
                { value: 'verificado', label: 'Verificado' },
                { value: 'verificar', label: 'Pendiente de verificar' },
                { value: 'suspendido', label: 'Suspendido' },
              ].map(opt => (
                <label key={opt.value} className="checkbox-filtro">
                  <input
                    type="checkbox"
                    checked={estadosSeleccionados.includes(opt.value)}
                    onChange={() => toggleArrayValue(estadosSeleccionados, opt.value, setEstadosSeleccionados)}
                  />
                  <span>{opt.label}</span>
                  <span className="checkbox-filtro-count">{conteoEstados[opt.value] || 0}</span>
                </label>
              ))}
            </div>

            <div className="grupo-filtro grupo-filtro-checkbox">
              <label>Departamento</label>
              {Object.entries(DEPTO_LABELS).map(([value, label]) => (
                <label key={value} className="checkbox-filtro">
                  <input
                    type="checkbox"
                    checked={deptosSeleccionados.includes(value)}
                    onChange={() => toggleArrayValue(deptosSeleccionados, value, setDeptosSeleccionados)}
                  />
                  <span>{label}</span>
                  <span className="checkbox-filtro-count">{conteoDeptos[value] || 0}</span>
                </label>
              ))}
            </div>
          </FiltroBusqueda>
        </aside>

        {seleccionados.size > 0 && (
          <div className="bulk-toolbar">
            <span className="bulk-toolbar-count">{seleccionados.size} seleccionado{seleccionados.size > 1 ? 's' : ''}</span>
            <select
              disabled={procesandoBulk}
              defaultValue=""
              onChange={e => { const v = e.target.value; e.target.value = ''; if (v) aplicarCambioMasivo('estado', v); }}
            >
              <option value="" disabled>Cambiar estado...</option>
              <option value="verificado">Verificar</option>
              <option value="suspendido">Suspender</option>
            </select>
            <button type="button" className="bulk-cancelar" onClick={() => setSeleccionados(new Set())} disabled={procesandoBulk}>
              Cancelar selección
            </button>
          </div>
        )}

        <div className="lista-usuarios">
          <label className="checkbox-filtro seleccionar-todos-usuarios">
            <input
              type="checkbox"
              checked={todosPaginaSeleccionados(usuariosActuales)}
              onChange={() => toggleSeleccionarTodos(usuariosActuales)}
              onClick={(e) => e.stopPropagation()}
            />
            <span>Seleccionar todos en esta página</span>
          </label>
          {usuariosActuales.length > 0 ? (
            usuariosActuales.map(user => (
              <div
                key={user.usuario}
                className={`tarjeta-usuario ${user.estado === 'suspendido' ? 'usuario-suspendido' : user.estado === 'verificar' ? 'usuario-pendiente' : ''}`}
                onClick={() => irADetalle(user.usuario)}
              >
                <div className="tarjeta-usuario-checkbox" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={seleccionados.has(user.usuario)}
                    onChange={() => toggleSeleccionado(user.usuario)}
                    aria-label={`Seleccionar usuario ${user.usuario}`}
                  />
                </div>

                <div className="imagen-usuario">
                  <img src={user.fotoPerfil ? (user.fotoPerfil.startsWith('http') ? user.fotoPerfil : `${IMAGE_DOMAIN}${user.fotoPerfil}`) : `${IMAGE_DOMAIN}/images/default-user.png`} alt="Foto de perfil" />
                </div>
                <div className="info-usuario">
                  <h3>{user.nombre} {user.apellidos}</h3>
                  <p className="nombre-usuario">@{user.usuario} <span className="email-usuario">· {user.email}</span></p>
                  <div className="datos-usuario">
                    {user.estado === 'verificar' && (
                      <p
                        className="pendiente-verificar"
                      >
                        Requiere atención
                      </p>
                    )}
                    <p className={`${user.role === 'admin' ? 'rol-admin' : user.role === 'tecnico' ? 'rol-tecnico' : 'rol-user'}`}>
                      {user.role === 'admin' ? 'Administrador' : user.role === 'tecnico' ? 'Técnico' : 'Usuario'}
                    </p>
                    <p className="fecha-union">
                      <span>Se unió el </span>
                      {new Date(user.createdAt).toLocaleString('es-ES', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                <div className="acciones-usuario">
                  <button>Gestionar perfil</button>
                </div>
              </div>
            ))
          ) : (
            <p className="mensaje-no-usuarios">No se encontraron usuarios.</p>
          )}
        </div>

        {/* Modal de recorte (fuera del Popup para no interferir con z-index) */}
        {showCropper && (
          <CropperModal
            rawImageSrc={rawImageSrc}
            crop={crop} setCrop={setCrop}
            zoom={zoom} setZoom={setZoom}
            onCropComplete={onCropComplete}
            onCancel={cancelCrop}
            onApply={applyCrop}
          />
        )}

        {/* Popup - Crear Usuario */}
        <Popup
          open={showCreatePopup}
          title="Crear nuevo usuario"
          onClose={cerrarCrear}
          showConfirm={false}
          footerActions={(
            <>
              <button className="boton-aceptar-popup" onClick={submitCrearUsuario} disabled={creating}>{creating ? 'Creando...' : 'Crear usuario'}</button>
            </>
          )}
        >
          <div className="form-crear-usuario">
            <div className="form-field">
              <label>Usuario (login) <span style={{ color: 'red' }}>*</span></label>
              <input placeholder="@johndoe" value={form.usuario} onChange={e => handleFormChange('usuario', e.target.value)} />
            </div>

            <div className="form-field">
              <label>Contraseña <span style={{ color: 'red' }}>*</span></label>
              <div className="password-row">
                <input type={showPassword ? 'text' : 'password'} placeholder="Contraseña segura" value={form.contrasena} onChange={e => handleFormChange('contrasena', e.target.value)} />
                <button type="button" className="boton-icono" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                  <img className="icono-ojo" src={showPassword ? '/images/ojo-abierto.svg' : '/images/ojo-cerrado.svg'} alt={showPassword ? 'Ojo abierto' : 'Ojo cerrado'} />
                </button>
              </div>
              <div className="password-actions">
                <button type="button" className="boton-primario" onClick={generarContrasena}>Generar contraseña</button>
              </div>
            </div>

            <div className="form-field">
              <label>Repetir contraseña <span style={{ color: 'red' }}>*</span></label>
              <div className="password-row">
                <input type={showRepeatPassword ? 'text' : 'password'} placeholder="Repetir contraseña" value={repetirContrasena} onChange={e => setRepetirContrasena(e.target.value)} />
                <button type="button" className="boton-icono" onClick={() => setShowRepeatPassword(v => !v)} aria-label={showRepeatPassword ? 'Ocultar repetir contraseña' : 'Mostrar repetir contraseña'}>
                  <img className="icono-ojo" src={showRepeatPassword ? '/images/ojo-abierto.svg' : '/images/ojo-cerrado.svg'} alt={showRepeatPassword ? 'Ojo abierto' : 'Ojo cerrado'} />
                </button>
              </div>
            </div>

            <div className="form-field">
              <label>Nombre <span style={{ color: 'red' }}>*</span></label>
              <input placeholder="Juan" value={form.nombre} onChange={e => handleFormChange('nombre', e.target.value)} />
            </div>

            <div className="form-field">
              <label>Apellidos <span style={{ color: 'red' }}>*</span></label>
              <input placeholder="Pérez" value={form.apellidos} onChange={e => handleFormChange('apellidos', e.target.value)} />
            </div>

            <div className="form-field">
              <label>Email <span style={{ color: 'red' }}>*</span></label>
              <input placeholder="nombre@dominio.com" value={form.email} onChange={e => handleFormChange('email', e.target.value)} />
            </div>

            <div className="form-field">
              <label>Teléfono <span style={{ color: 'red' }}>*</span></label>
              <input placeholder="+34 600 000 000" value={form.telefono} onChange={e => handleFormChange('telefono', e.target.value)} />
            </div>

            <div className="form-field">
              <label>Web del cliente <span style={{ color: 'red' }}>*</span></label>
              <input placeholder="https://cliente.com" value={form.webs_del_cliente} onChange={e => handleFormChange('webs_del_cliente', e.target.value)} />
            </div>

            <div className="form-field">
              <label>Rol <span style={{ color: 'red' }}>*</span></label>
              <select value={form.role} onChange={e => handleFormChange('role', e.target.value)} disabled={usuarioRole === 'tecnico'}>
                <option value="user">Usuario</option>
                <option value="tecnico">Técnico</option>
                <option value="admin">Administrador</option>
              </select>
              {usuarioRole === 'tecnico' && <small>Los técnicos sólo pueden crear usuarios con rol <strong>user</strong>.</small>}
            </div>
            <div className="form-field">
              <label>Foto de perfil <small style={{ marginLeft: 6 }}>(opcional)</small></label>
              <input
                ref={fotoInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <div className="foto-perfil-uploader">
                {fotoPreview ? (
                  <div className="foto-preview-wrap">
                    <img src={fotoPreview} alt="Vista previa" className="foto-preview-circle" />
                    <div className="foto-preview-actions">
                      <button type="button" className="btn-foto-change" onClick={() => fotoInputRef.current?.click()}>
                        Cambiar foto
                      </button>
                      <button type="button" className="btn-foto-remove" onClick={() => { setFotoFile(null); setFotoPreview(null); }}>
                        Quitar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="foto-upload-placeholder" onClick={() => fotoInputRef.current?.click()}>
                    <div className="foto-upload-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M12 16v-4m0 0V8m0 4H8m4 0h4" strokeLinecap="round" />
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    </div>
                    <span className="foto-upload-text">Haz clic para subir una foto</span>
                    <span className="foto-upload-hint">JPG, PNG o WEBP</span>
                  </div>
                )}
              </div>
            </div>

            
          </div>
        </Popup>

        {/* Popup - Confirmación */}
        <Popup
          open={showConfirmPopup}
          title="Usuario creado"
          onClose={cerrarConfirm}
          showConfirm={false}
          footerActions={(
            <>
              <button className="boton-aceptar-popup" onClick={() => copiarTextoBienvenida(document.getElementById('textoBienvenida').value)}>Copiar texto</button>
            </>
          )}
        >
          {createdResult ? (
            <div className="form-crear-usuario">
              <div className="form-field">
                <label>Resumen</label>
                <p>Usuario creado correctamente. Por favor, proporciona el siguiente mensaje al cliente.</p>
              </div>

              <div className="form-field">
                <label>Texto para enviar (email / WhatsApp)</label>
                <textarea
                  defaultValue={(() => {
                    const nombre = createdResult.data.nombre || '';
                    const usuario = createdResult.data.usuario || '';
                    const email = createdResult.data.email || '';
                    const contrasena = createdResult.displayPassword || '';
                    return `Hola ${nombre},\n\nHemos creado tu cuenta de acceso a nuestro soporte técnico con los siguientes datos:\n\nUsuario: ${usuario}\nEmail asociado: ${email}\nContraseña: ${contrasena}\n\nPuedes acceder al sistema desde:\n👉 https://soporte.agenciaadhoc.com/iniciar-sesion\n\nUna vez dentro podrás crear nuevos tickets y consultar el historial de solicitudes asociadas a tu cuenta.\n\nPor motivos de seguridad, te recomendamos cambiar la contraseña en tu primer inicio de sesión.\n\nLa validez de tu cuenta estará vinculada a la duración de tu contrato de servicios con nosotros.\n\nGracias por confiar en nosotros.\n\nAtentamente,\nEquipo de Soporte`;
                  })()}
                  rows={10}
                  id="textoBienvenida"
                />
              </div>
            </div>
          ) : (
            <p>Usuario creado.</p>
          )}
        </Popup>
        <div className="paginacion-usuarios">
          <button className='anterior' onClick={() => cambiarPagina(paginaActual - 1)} disabled={paginaActual === 1}>
            Anterior
          </button>
          <span>Página {paginaActual} de {totalPaginas}</span>
          <button className='siguiente' onClick={() => cambiarPagina(paginaActual + 1)} disabled={paginaActual === totalPaginas}>
            Siguiente
          </button>
        </div>
      </div>
    </main>
  );
};

export default GestorUsuarios;
